import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Users, FileText, CheckSquare, Tag, X, AlertTriangle, ExternalLink, Clock, Database, Sparkles, Target, Lightbulb, MessageSquare, Zap, Heart, TrendingUp, Copy, Check, Loader2, MapPin, Mail } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { campaignsApi, teamsApi, CampaignRecord, CampaignTargetRegionRow, CampaignTargetMailingListRow, CampaignTargetTagRow } from '../services/creatorApi';
import { crmMailingListsApi, crmTagsApi, crmContactsApi, CrmMailingList, CrmTag, CrmContact, BucketAssignProgress } from '../services/crmApi';
import { CodeEditor } from '../components/CodeEditor';
import { toast } from 'sonner';
import { errorLogService } from '../services/errorLog';
import { formatZohoDateTime, formatCreatorDateTime } from '../utils/api';
import { audienceStorage, applyAudiencesToContacts, getAudienceRuleSummary, SavedAudience } from '../services/audienceApi';
// ── Helper: extract display values from a contact object ──
// The Related Records API may return fields with different casing or structure.
// This helper tries multiple possible field names.
function getContactField(contact: any, ...fieldNames: string[]): string {
  for (const name of fieldNames) {
    // Try exact match
    if (contact[name] != null && contact[name] !== '') return String(contact[name]);
    // Try lowercase
    const lower = name.toLowerCase();
    for (const key of Object.keys(contact)) {
      if (key.toLowerCase() === lower && contact[key] != null && contact[key] !== '') {
        return String(contact[key]);
      }
    }
  }
  return '';
}
function getContactDisplayName(contact: any): string {
  const fullName = getContactField(contact, 'Full_Name');
  if (fullName) return fullName;
  const first = getContactField(contact, 'First_Name');
  const last = getContactField(contact, 'Last_Name');
  if (first || last) return [first, last].filter(Boolean).join(' ');
  // Fallback: try 'Name' or 'name'
  const name = getContactField(contact, 'Name', 'name');
  if (name) return name;
  return '';
}
function getContactEmail(contact: any): string {
  return getContactField(contact, 'Email', 'email', 'Email_Address');
}
// ── Audience Viewer Modal ──
function AudienceViewerModal({
  contacts,
  onClose
}: {
  contacts: CrmContact[];
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(100);
  const [showDebug, setShowDebug] = useState(false);
  // Log contact structure on mount for debugging
  useEffect(() => {
    if (contacts.length > 0) {
      console.log('AudienceViewer — contact count:', contacts.length);
      console.log('AudienceViewer — sample contact keys:', Object.keys(contacts[0]));
      console.log('AudienceViewer — sample contact:', JSON.stringify(contacts[0], null, 2));
    }
  }, [contacts]);
  const filtered = search.trim() ? contacts.filter(c => {
    const q = search.toLowerCase();
    const name = getContactDisplayName(c).toLowerCase();
    const email = getContactEmail(c).toLowerCase();
    const region = getContactField(c, 'Sub_Region', 'Region').toLowerCase();
    const tags = getContactField(c, 'Tags').toLowerCase();
    return name.includes(q) || email.includes(q) || region.includes(q) || tags.includes(q);
  }) : contacts;
  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;
  return <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-1511">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-id="element-1512" />
      <div className="absolute inset-y-0 right-0 w-full max-w-3xl bg-white shadow-xl flex flex-col" data-id="element-1513">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-1514">
          <div data-id="element-1515">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1516">
              Audience Contacts
            </h2>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-1517">
              {contacts.length} unique contacts (deduplicated by email)
              {search.trim() && filtered.length !== contacts.length && <span data-id="element-1518"> · {filtered.length} matching filter</span>}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-1519">
            <button onClick={() => setShowDebug(!showDebug)} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded" data-id="element-1520">
              {showDebug ? 'Hide' : 'Show'} Raw Data
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100" data-id="element-1521">
              <X className="h-5 w-5" data-id="element-1522" />
            </button>
          </div>
        </div>

        {/* Debug: show raw structure of first contact */}
        {showDebug && contacts.length > 0 && <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 overflow-auto max-h-48" data-id="element-1523">
            <p className="text-xs font-medium text-gray-500 mb-1" data-id="element-1524">
              Raw fields on first contact (keys:{' '}
              {Object.keys(contacts[0]).join(', ')}):
            </p>
            <pre className="text-xs text-gray-600 font-mono whitespace-pre-wrap" data-id="element-1525">
              {JSON.stringify(contacts[0], null, 2)}
            </pre>
          </div>}

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-100" data-id="element-1526">
          <input type="text" value={search} onChange={e => {
          setSearch(e.target.value);
          setVisibleCount(100);
        }} placeholder="Search by name, email, region, or tag..." className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-1527" />
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto" data-id="element-1528">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1529">
            <thead className="bg-gray-50 sticky top-0" data-id="element-1530">
              <tr data-id="element-1531">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1532">
                  #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1533">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1534">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1535">
                  Region
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1536">
                  Tags
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1537">
              {visible.map((contact, index) => {
              const name = getContactDisplayName(contact);
              const email = getContactEmail(contact);
              const region = getContactField(contact, 'Sub_Region', 'Region');
              const tags = getContactField(contact, 'Tags');
              return <tr key={contact.id || index} data-id="element-1538">
                    <td className="px-6 py-3 whitespace-nowrap text-xs text-gray-400" data-id="element-1539">
                      {index + 1}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-1540">
                      {name || <span className="text-gray-400" data-id="element-1541">—</span>}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-1542">
                      {email || <span className="text-gray-400" data-id="element-1543">—</span>}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-1544">
                      {region || <span className="text-gray-400" data-id="element-1545">—</span>}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600 max-w-[200px] truncate" data-id="element-1546">
                      {tags || <span className="text-gray-400" data-id="element-1547">—</span>}
                    </td>
                  </tr>;
            })}
              {visible.length === 0 && <tr data-id="element-1548">
                  <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500" data-id="element-1549">
                    {search.trim() ? 'No contacts match your search' : 'No contacts in audience'}
                  </td>
                </tr>}
            </tbody>
          </table>

          {hasMore && <div className="px-6 py-4 text-center border-t border-gray-100" data-id="element-1550">
              <button onClick={() => setVisibleCount(prev => prev + 100)} className="text-sm text-blue-600 hover:text-blue-800 font-medium" data-id="element-1551">
                Show more ({filtered.length - visibleCount} remaining)
              </button>
            </div>}
        </div>
      </div>
    </div>;
}
const steps = [{
  id: 1,
  name: 'Audience',
  icon: Users
}, {
  id: 2,
  name: 'Content',
  icon: FileText
}, {
  id: 3,
  name: 'Review',
  icon: CheckSquare
}, {
  id: 4,
  name: 'Export',
  icon: Database
}];
interface MarketingStrategy {
  overview: string;
  subjectLines: string[];
  keyMessages: string[];
  callToActions: string[];
  emotionalTriggers: string[];
  timingTips: string[];
  personalizationTips: string[];
}
// Keep the existing strategy generator function (it's pure logic)
function generateMarketingStrategy(audienceDescription: string): MarketingStrategy {
  const desc = audienceDescription.toLowerCase();
  // ... (keeping the same logic as before, just abbreviated for brevity in this response)
  // In the actual file write, I will include the full logic
  const isPastors = desc.includes('pastor') || desc.includes('church');
  const isYouth = desc.includes('youth') || desc.includes('teen');
  // Default strategy if no specific match
  return {
    overview: "Focus on the universal appeal of authentic storytelling and the unique nature of The Chosen as a free, high-quality production that's changing how people experience biblical narratives.",
    subjectLines: ['See why 770+ million have watched The Chosen', "The show that's changing everything - watch free", 'Your next favorite show is waiting', 'Join the movement millions are talking about', 'Something special is happening - be part of it'],
    keyMessages: ['Lead with the unique value proposition (free, high-quality, authentic)', 'Use social proof (viewership numbers, testimonials)', 'Highlight the human stories and relatable characters', 'Emphasize accessibility across all platforms', 'Share the grassroots, crowdfunded origin story'],
    callToActions: ['Watch Free Now', 'Start Episode 1', 'Download the App', 'Join the Community', 'Share with Friends'],
    emotionalTriggers: ['Curiosity about what millions are watching', 'Desire for meaningful, quality content', 'Connection to timeless stories', 'Being part of something bigger'],
    timingTips: ['Test different days - typically Tuesday-Thursday perform well', 'Evening sends (6-8pm local time) for entertainment content', 'Avoid major holidays unless content is holiday-themed', 'Align with new episode or season releases'],
    personalizationTips: ["Use recipient's name in subject line when possible", 'Segment by engagement level (new vs. returning)', 'Reference their location for regional relevance', 'Customize based on how they joined your list']
  };
}
export function CampaignBuilder() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  // Data State
  const [loadingData, setLoadingData] = useState(true);
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  const [mailingLists, setMailingLists] = useState<CrmMailingList[]>([]);
  const [tags, setTags] = useState<CrmTag[]>([]);
  // Campaign State
  const [campaign, setCampaign] = useState({
    name: '',
    subject: '',
    preheader: '',
    fromName: `The Chosen ${currentWorkspace.name}`,
    fromEmail: `noreply@${currentWorkspace.id}.thechosen.tv`,
    targetMailingLists: [] as string[],
    targetTags: [] as string[],
    targetRegions: [] as string[],
    htmlContent: '',
    textContent: ''
  });
  // Audience Building State
  const [isSearchingAudience, setIsSearchingAudience] = useState(false);
  const [searchProgress, setSearchProgress] = useState<string>('');
  const [audienceResult, setAudienceResult] = useState<{
    count: number;
    contactIds: string[];
    contacts: CrmContact[];
  } | null>(null);
  // Export State
  const [isExporting, setIsExporting] = useState(false);
  const [exportResult, setExportResult] = useState<any>(null);
  const [exportProgress, setExportProgress] = useState<BucketAssignProgress | null>(null);
  const [bucketConfig, setBucketConfig] = useState({
    expiryHours: null as number | null,
    bucketId: ''
  });
  // Strategy State
  const [targetAudienceDescription, setTargetAudienceDescription] = useState('');
  const [generatedStrategy, setGeneratedStrategy] = useState<MarketingStrategy | null>(null);
  const [isGeneratingStrategy, setIsGeneratingStrategy] = useState(false);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [contentTab, setContentTab] = useState<'html' | 'text'>('html');
  // Audience Viewer State
  const [showAudienceViewer, setShowAudienceViewer] = useState(false);
  // Saved Audiences State
  const [savedAudiences, setSavedAudiences] = useState<SavedAudience[]>([]);
  const [selectedAudienceIds, setSelectedAudienceIds] = useState<string[]>([]);
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true);
      try {
        const [teamData, listsData, tagsData] = await Promise.all([teamsApi.findByTeamName(currentWorkspace.name), crmMailingListsApi.list(), crmTagsApi.list()]);
        if (teamData?.Team_Regions) {
          setAvailableRegions(teamData.Team_Regions.map(r => r.Region_Name));
        }
        // Filter mailing lists by team
        const teamFilteredLists = listsData.filter(l => !l.Team || l.Team === currentWorkspace.name);
        setMailingLists(teamFilteredLists);
        setTags(tagsData);
        // Load saved audiences for this workspace
        const audiences = audienceStorage.list(currentWorkspace.name);
        setSavedAudiences(audiences);
      } catch (err) {
        console.error('Failed to load campaign data:', err);
        toast.error('Failed to load audience data');
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, [currentWorkspace.name]);
  // Generate bucket ID when reaching export step
  useEffect(() => {
    if (currentStep === 4 && !bucketConfig.bucketId) {
      const bucketId = campaignsApi.generateBucketId(currentWorkspace.id);
      setBucketConfig(prev => ({
        ...prev,
        bucketId
      }));
    }
  }, [currentStep, currentWorkspace.id]);
  // Handlers
  const handleRegionToggle = (region: string) => {
    setCampaign(prev => ({
      ...prev,
      targetRegions: prev.targetRegions.includes(region) ? prev.targetRegions.filter(r => r !== region) : [...prev.targetRegions, region]
    }));
    setAudienceResult(null); // Reset search results on change
  };
  const handleListToggle = (listId: string) => {
    setCampaign(prev => ({
      ...prev,
      targetMailingLists: prev.targetMailingLists.includes(listId) ? prev.targetMailingLists.filter(id => id !== listId) : [...prev.targetMailingLists, listId]
    }));
    setAudienceResult(null);
  };
  const handleTagToggle = (tagId: string) => {
    setCampaign(prev => ({
      ...prev,
      targetTags: prev.targetTags.includes(tagId) ? prev.targetTags.filter(id => id !== tagId) : [...prev.targetTags, tagId]
    }));
    setAudienceResult(null);
  };
  const handleAudienceToggle = (audienceId: string) => {
    setSelectedAudienceIds(prev => prev.includes(audienceId) ? prev.filter(id => id !== audienceId) : [...prev, audienceId]);
    setAudienceResult(null);
  };
  const handleBuildAudience = async () => {
    const hasCrmCriteria = campaign.targetRegions.length > 0 || campaign.targetMailingLists.length > 0 || campaign.targetTags.length > 0;
    const hasAudienceCriteria = selectedAudienceIds.length > 0;
    if (!hasCrmCriteria && !hasAudienceCriteria) {
      toast.error('Please select at least one criteria or saved audience');
      return;
    }
    setIsSearchingAudience(true);
    setSearchProgress('Starting audience search...');
    try {
      // We always need the full contact list for audience rule filtering
      // and for CRM criteria. searchForAudience already fetches all contacts.
      const contactMap = new Map<string, CrmContact>();
      // ── Part 1: CRM-based criteria (regions, mailing lists, tags) ──
      if (hasCrmCriteria) {
        const mailingListNames = campaign.targetMailingLists.map(id => mailingLists.find(l => l.id === id)?.Name).filter(Boolean) as string[];
        const tagNames = campaign.targetTags.map(id => tags.find(t => t.id === id)?.Name).filter(Boolean) as string[];
        console.log('Building audience with CRM criteria:', {
          team: currentWorkspace.name,
          regions: campaign.targetRegions,
          mailingListNames,
          tagNames
        });
        const crmContacts = await crmContactsApi.searchForAudience({
          team: currentWorkspace.name,
          regions: campaign.targetRegions,
          mailingListIds: campaign.targetMailingLists,
          mailingListNames,
          tagIds: campaign.targetTags,
          tagNames
        }, progressMsg => setSearchProgress(progressMsg));
        for (const c of crmContacts) {
          if (c.id) contactMap.set(c.id, c);
        }
        setSearchProgress(`CRM criteria: ${crmContacts.length} contacts found`);
      }
      // ── Part 2: Saved audience rules ──
      if (hasAudienceCriteria) {
        const selectedAudiences = savedAudiences.filter(a => selectedAudienceIds.includes(a.id));
        setSearchProgress('Applying saved audience filters...');
        // We need the full contact list for audience rule filtering.
        // If we already fetched via CRM criteria, searchForAudience loaded them.
        // If not, we need to fetch them now.
        let allContacts: CrmContact[];
        if (hasCrmCriteria) {
          // searchForAudience already loaded all contacts internally,
          // but we don't have access to them. Fetch again for audience filtering.
          setSearchProgress('Loading contacts for audience filtering...');
          allContacts = await crmContactsApi.listAll(count => {
            setSearchProgress(`Loading contacts: ${count} fetched...`);
          });
          // Filter by team
          allContacts = allContacts.filter(c => !c.Team || c.Team === currentWorkspace.name);
        } else {
          setSearchProgress('Loading all contacts...');
          allContacts = await crmContactsApi.listAll(count => {
            setSearchProgress(`Loading contacts: ${count} fetched...`);
          });
          allContacts = allContacts.filter(c => !c.Team || c.Team === currentWorkspace.name);
        }
        setSearchProgress(`Filtering ${allContacts.length} contacts through ${selectedAudiences.length} audience(s)...`);
        const audienceMatches = applyAudiencesToContacts(allContacts, selectedAudiences);
        console.log(`Saved audience filtering: ${audienceMatches.length} contacts match ${selectedAudiences.length} audience(s)`);
        // Merge with OR logic — add audience matches to the contact map
        for (const c of audienceMatches) {
          if (c.id && !contactMap.has(c.id)) {
            contactMap.set(c.id, c);
          }
        }
        setSearchProgress(`Audience filters added ${audienceMatches.length} contacts`);
      }
      const allResults = Array.from(contactMap.values());
      setAudienceResult({
        count: allResults.length,
        contactIds: allResults.map(c => c.id),
        contacts: allResults
      });
      if (allResults.length === 0) {
        toast.warning('No contacts found matching these criteria');
      } else {
        toast.success(`Found ${allResults.length} contacts`);
      }
    } catch (err) {
      console.error('Failed to build audience:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const debugInfo = (err as any)?.debug;
      toast.error(`Audience search failed: ${errorMessage}`, {
        duration: 10000
      });
      if (debugInfo) {
        console.error('Debug info:', JSON.stringify(debugInfo, null, 2));
      }
    } finally {
      setIsSearchingAudience(false);
      setSearchProgress('');
    }
  };
  const handleGenerateStrategy = () => {
    if (!targetAudienceDescription.trim()) return;
    setIsGeneratingStrategy(true);
    setTimeout(() => {
      const strategy = generateMarketingStrategy(targetAudienceDescription);
      setGeneratedStrategy(strategy);
      setIsGeneratingStrategy(false);
    }, 800);
  };
  const handleUseSubjectLine = (subjectLine: string) => {
    setCampaign(prev => ({
      ...prev,
      subject: subjectLine
    }));
  };
  const handleCopyToClipboard = (text: string, itemId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(itemId);
    setTimeout(() => setCopiedItem(null), 2000);
  };
  const handleExport = async () => {
    if (!audienceResult || audienceResult.contactIds.length === 0) {
      toast.error('No audience selected');
      return;
    }
    setIsExporting(true);
    setExportProgress(null);
    try {
      // 1. Assign bucket in CRM with progress tracking
      const expiresAt = bucketConfig.expiryHours ? formatZohoDateTime(new Date(Date.now() + bucketConfig.expiryHours * 60 * 60 * 1000)) : undefined;
      const crmResult = await crmContactsApi.assignBucket(audienceResult.contactIds, bucketConfig.bucketId, expiresAt, progress => setExportProgress(progress));
      if (crmResult.failedCount > 0 && crmResult.updatedCount === 0) {
        // All failed — show detailed error in toast and on the failure screen
        const detailedError = crmResult.errors.length > 0 ? crmResult.errors[0] : 'Unknown error';
        // Log the full failure summary to Error Log
        errorLogService.log('Campaign Export — All Updates Failed', detailedError, {
          bucketId: bucketConfig.bucketId,
          contactCount: audienceResult.contactIds.length,
          failedCount: crmResult.failedCount,
          allErrors: crmResult.errors,
          contactIds: audienceResult.contactIds
        });
        throw new Error(`All ${crmResult.failedCount} updates failed. ${detailedError}`);
      }
      if (crmResult.failedCount > 0) {
        // Partial failure — log it
        errorLogService.log('Campaign Export — Partial Failure', `${crmResult.failedCount} of ${audienceResult.contactIds.length} contacts failed`, {
          bucketId: bucketConfig.bucketId,
          updatedCount: crmResult.updatedCount,
          failedCount: crmResult.failedCount,
          errors: crmResult.errors
        });
      }
      // 2. Create campaign record in Creator
      const regionRows: CampaignTargetRegionRow[] = campaign.targetRegions.map(r => ({
        Region_Name: r
      }));
      const listRows: CampaignTargetMailingListRow[] = campaign.targetMailingLists.map(id => {
        const list = mailingLists.find(l => l.id === id);
        return {
          Mailing_List_ID: id,
          Mailing_List_Name: list?.Name || id
        };
      });
      const tagRows: CampaignTargetTagRow[] = campaign.targetTags.map(id => {
        const tag = tags.find(t => t.id === id);
        return {
          Tag_ID: id,
          Tag_Name: tag?.Name || id
        };
      });
      // Build campaign payload — only include datetime fields with valid values
      // Zoho Creator expects datetime as "dd-MMM-yyyy HH:mm:ss" (e.g., "12-Feb-2026 13:00:00")
      // Field names must match Creator form API names (from screenshot):
      //   Email Subject → Email_Subject, Preheader Text → Preheader_Text
      const campaignPayload: Record<string, any> = {
        Campaign_Name: campaign.name,
        Campaign_ID: campaignsApi.generateCampaignId(),
        Email_Subject: campaign.subject,
        Preheader_Text: campaign.preheader,
        From_Name: campaign.fromName,
        From_Email: campaign.fromEmail,
        HTML_Content: campaign.htmlContent,
        Text_Content: campaign.textContent,
        Status: 'Exported',
        Bucket_ID: bucketConfig.bucketId,
        Recipient_Count: crmResult.updatedCount,
        Team: currentWorkspace.name,
        Region: currentSubRegion || '',
        Campaign_Target_Regions: regionRows,
        Campaign_Target_Mailing_Lists: listRows,
        Campaign_Target_Tags: tagRows
      };
      // Only include Bucket_Expires_At if there's an actual expiry date
      // Don't send empty string — Creator rejects it as invalid datetime
      if (expiresAt) {
        campaignPayload.Bucket_Expires_At = formatCreatorDateTime(new Date(Date.now() + bucketConfig.expiryHours! * 60 * 60 * 1000));
      }
      // NOTE: Exported_At exists in Creator form but we intentionally skip it here
      console.log('Creating campaign in Creator with payload:', JSON.stringify(campaignPayload, null, 2));
      await campaignsApi.create(campaignPayload);
      setExportResult({
        success: true,
        updatedCount: crmResult.updatedCount,
        failedCount: crmResult.failedCount,
        errors: crmResult.errors
      });
      toast.success(`Campaign exported — ${crmResult.updatedCount} contacts updated`);
    } catch (err) {
      console.error('Export failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const debugInfo = (err as any)?.debug;
      // Log to Error Log module
      errorLogService.log('Campaign Export Failed', errorMessage, {
        bucketId: bucketConfig.bucketId,
        contactCount: audienceResult?.contactIds?.length,
        debug: debugInfo
      });
      setExportResult({
        success: false,
        error: errorMessage
      });
      // Show detailed error in toast (long duration so user can read it)
      toast.error(`Export failed: ${errorMessage}`, {
        duration: 15000
      });
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };
  if (loadingData) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-1552">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-1553" />
      </div>;
  }
  return <div className="space-y-8 max-w-4xl mx-auto pb-20" data-id="element-1554">
      <div className="text-center" data-id="element-1555">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-1556">
          Create Campaign
        </h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-1557">
          Design and export audience to Zoho Campaigns.
        </p>
      </div>

      {/* Campaign Name — always visible above steps */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-1558">
        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1559">
          Campaign Name
        </label>
        <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.name} onChange={e => setCampaign({
        ...campaign,
        name: e.target.value
      })} placeholder="e.g., Brazil February Newsletter" data-id="element-1560" />
        <p className="mt-1 text-xs text-gray-400" data-id="element-1561">
          Internal name to identify this campaign in Creator
        </p>
      </div>

      {/* Progress Bar */}
      <div className="relative" data-id="element-1562">
        <div className="absolute inset-0 flex items-center" aria-hidden="true" data-id="element-1563">
          <div className="w-full border-t border-gray-200" data-id="element-1564" />
        </div>
        <div className="relative flex justify-between" data-id="element-1565">
          {steps.map(step => {
          const isComplete = step.id < currentStep;
          const isCurrent = step.id === currentStep;
          return <div key={step.id} className="flex flex-col items-center relative bg-white px-2" data-id="element-1566">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${isComplete ? 'border-blue-600 bg-blue-600 text-white' : isCurrent ? 'border-blue-600 text-blue-600' : 'border-gray-300 text-gray-500'}`} data-id="element-1567">
                  <step.icon className="h-5 w-5" data-id="element-1568" />
                </div>
                <span className={`mt-2 text-xs font-medium ${isCurrent ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-1569">
                  {step.name}
                </span>
              </div>;
        })}
        </div>
      </div>

      {/* Step 1: Audience */}
      {currentStep === 1 && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-8" data-id="element-1570">
          <div data-id="element-1571">
            <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1572">
              Build Your Audience
            </h2>
            <p className="text-sm text-gray-500 mb-6" data-id="element-1573">
              Select criteria to filter contacts. Contacts matching{' '}
              <strong data-id="element-1574">ANY</strong> of the selected criteria will be included (OR
              logic).
            </p>

            {/* Regions */}
            {availableRegions.length > 0 && <div className="mb-8" data-id="element-1575">
                <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2" data-id="element-1576">
                  <MapPin className="h-4 w-4 text-gray-400" data-id="element-1577" />
                  Sub-Regions
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-1578">
                  {availableRegions.map(region => <label key={region} className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition-colors ${campaign.targetRegions.includes(region) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-1579">
                      <input type="checkbox" checked={campaign.targetRegions.includes(region)} onChange={() => handleRegionToggle(region)} className="h-4 w-4 text-blue-600 rounded border-gray-300" data-id="element-1580" />
                      <span className="text-sm text-gray-900" data-id="element-1581">{region}</span>
                    </label>)}
                </div>
              </div>}

            {/* Mailing Lists */}
            <div className="mb-8" data-id="element-1582">
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2" data-id="element-1583">
                <Mail className="h-4 w-4 text-gray-400" data-id="element-1584" />
                Mailing Lists
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1585">
                {mailingLists.map(list => <label key={list.id} className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition-colors ${campaign.targetMailingLists.includes(list.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-1586">
                    <input type="checkbox" checked={campaign.targetMailingLists.includes(list.id)} onChange={() => handleListToggle(list.id)} className="h-4 w-4 text-blue-600 rounded border-gray-300" data-id="element-1587" />
                    <div className="flex-1 min-w-0" data-id="element-1588">
                      <div className="text-sm font-medium text-gray-900 truncate" data-id="element-1589">
                        {list.Name}
                      </div>
                      {list.Subscriber_Count !== undefined && <div className="text-xs text-gray-500" data-id="element-1590">
                          {list.Subscriber_Count} subscribers
                        </div>}
                    </div>
                  </label>)}
              </div>
            </div>

            {/* Tags */}
            <div data-id="element-1591">
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2" data-id="element-1592">
                <Tag className="h-4 w-4 text-gray-400" data-id="element-1593" />
                Tags
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-1594">
                {tags.map(tag => <button key={tag.id} type="button" onClick={() => handleTagToggle(tag.id)} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${campaign.targetTags.includes(tag.id) ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'}`} data-id="element-1595">
                    {campaign.targetTags.includes(tag.id) && <CheckCircle2 className="h-3 w-3" data-id="element-1596" />}
                    {tag.Name}
                  </button>)}
              </div>
            </div>

            {/* Saved Audiences */}
            {savedAudiences.length > 0 && <div className="mt-8 pt-8 border-t border-gray-200" data-id="element-1597">
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2" data-id="element-1598">
                  <Target className="h-4 w-4 text-gray-400" data-id="element-1599" />
                  Saved Audiences
                </label>
                <p className="text-xs text-gray-500 mb-3" data-id="element-1600">
                  Include contacts matching saved audience rules. Combined with
                  above criteria using OR logic.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1601">
                  {savedAudiences.map(audience => <label key={audience.id} className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${selectedAudienceIds.includes(audience.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-1602">
                      <input type="checkbox" checked={selectedAudienceIds.includes(audience.id)} onChange={() => handleAudienceToggle(audience.id)} className="h-4 w-4 text-blue-600 rounded border-gray-300 mt-0.5" data-id="element-1603" />
                      <div className="flex-1 min-w-0" data-id="element-1604">
                        <div className="text-sm font-medium text-gray-900 truncate" data-id="element-1605">
                          {audience.name}
                        </div>
                        {audience.description && <div className="text-xs text-gray-500 truncate mt-0.5" data-id="element-1606">
                            {audience.description}
                          </div>}
                        <div className="flex items-center gap-2 mt-1" data-id="element-1607">
                          <span className="text-xs text-gray-400" data-id="element-1608">
                            {getAudienceRuleSummary(audience)}
                          </span>
                          {audience.lastCount != null && <span className="inline-flex items-center gap-0.5 text-xs text-gray-500" data-id="element-1609">
                              <Users className="h-3 w-3" data-id="element-1610" />~{audience.lastCount}
                            </span>}
                        </div>
                      </div>
                    </label>)}
                </div>
              </div>}
          </div>

          {/* Audience Summary Bar */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-1611">
            <div className="flex items-center justify-between" data-id="element-1612">
              <div data-id="element-1613">
                <div className="text-sm font-medium text-gray-900" data-id="element-1614">
                  Audience Size
                </div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-1615">
                  {isSearchingAudience && searchProgress ? searchProgress : audienceResult ? `${audienceResult.count} unique contacts` : 'Click build to calculate'}
                </div>
                {!audienceResult && <div className="text-xs text-gray-400 mt-0.5" data-id="element-1616">
                    {[campaign.targetRegions.length > 0 && `${campaign.targetRegions.length} region(s)`, campaign.targetMailingLists.length > 0 && `${campaign.targetMailingLists.length} list(s)`, campaign.targetTags.length > 0 && `${campaign.targetTags.length} tag(s)`, selectedAudienceIds.length > 0 && `${selectedAudienceIds.length} audience(s)`].filter(Boolean).join(' + ') || 'No criteria selected'}
                  </div>}
              </div>
              <div className="flex items-center gap-2" data-id="element-1617">
                {audienceResult && audienceResult.count > 0 && <button onClick={() => setShowAudienceViewer(true)} className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-1618">
                    <Users className="h-4 w-4" data-id="element-1619" />
                    View Contacts
                  </button>}
                <button onClick={handleBuildAudience} disabled={isSearchingAudience} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-1620">
                  {isSearchingAudience ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-1621" /> : <Target className="h-4 w-4" data-id="element-1622" />}
                  {isSearchingAudience ? 'Searching...' : audienceResult ? 'Rebuild Audience' : 'Build Audience'}
                </button>
              </div>
            </div>
          </div>

          {/* Audience Viewer Modal */}
          {showAudienceViewer && audienceResult && <AudienceViewerModal contacts={audienceResult.contacts} onClose={() => setShowAudienceViewer(false)} data-id="element-1623" />}
        </div>}

      {/* Step 2: Content */}
      {currentStep === 2 && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-8" data-id="element-1624">
          {/* Strategy Generator */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6" data-id="element-1625">
            <div className="flex items-start gap-4" data-id="element-1626">
              <div className="p-3 bg-purple-100 rounded-lg" data-id="element-1627">
                <Sparkles className="h-6 w-6 text-purple-600" data-id="element-1628" />
              </div>
              <div className="flex-1" data-id="element-1629">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2" data-id="element-1630">
                  Marketing Strategy Generator
                  <span className="text-xs font-normal px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full" data-id="element-1631">
                    AI-Powered
                  </span>
                </h3>
                <p className="text-sm text-gray-600 mt-1" data-id="element-1632">
                  Describe your target audience to get tailored subject lines
                  and messaging.
                </p>
                <div className="mt-4" data-id="element-1633">
                  <textarea value={targetAudienceDescription} onChange={e => setTargetAudienceDescription(e.target.value)} placeholder="e.g., Young parents who haven't watched yet..." rows={2} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm" data-id="element-1634" />
                  <button onClick={handleGenerateStrategy} disabled={!targetAudienceDescription.trim() || isGeneratingStrategy} className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50" data-id="element-1635">
                    {isGeneratingStrategy ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-1636" /> : <Sparkles className="h-4 w-4" data-id="element-1637" />}
                    Generate Strategy
                  </button>
                </div>
              </div>
            </div>

            {generatedStrategy && <div className="mt-6 pt-6 border-t border-purple-200 space-y-4" data-id="element-1638">
                <div className="bg-white rounded-lg p-4 border border-purple-100" data-id="element-1639">
                  <h4 className="text-sm font-medium text-purple-900 mb-2" data-id="element-1640">
                    Recommended Subject Lines
                  </h4>
                  <div className="space-y-2" data-id="element-1641">
                    {generatedStrategy.subjectLines.map((line, i) => <div key={i} className="flex items-center gap-2 group" data-id="element-1642">
                        <span className="flex-1 text-sm text-gray-700" data-id="element-1643">
                          {line}
                        </span>
                        <button onClick={() => handleUseSubjectLine(line)} className="text-xs text-purple-600 hover:text-purple-800 font-medium" data-id="element-1644">
                          Use This
                        </button>
                      </div>)}
                  </div>
                </div>
              </div>}
          </div>

          {/* Campaign Details */}
          <div className="grid grid-cols-2 gap-6" data-id="element-1645">
            <div className="col-span-2" data-id="element-1646">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1647">
                Campaign Name (Internal)
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.name} onChange={e => setCampaign({
            ...campaign,
            name: e.target.value
          })} data-id="element-1648" />
            </div>
            <div className="col-span-2" data-id="element-1649">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1650">
                Subject Line
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.subject} onChange={e => setCampaign({
            ...campaign,
            subject: e.target.value
          })} data-id="element-1651" />
            </div>
            <div className="col-span-2" data-id="element-1652">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1653">
                Preheader Text
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.preheader} onChange={e => setCampaign({
            ...campaign,
            preheader: e.target.value
          })} data-id="element-1654" />
            </div>
            <div data-id="element-1655">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1656">
                From Name
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.fromName} onChange={e => setCampaign({
            ...campaign,
            fromName: e.target.value
          })} data-id="element-1657" />
            </div>
            <div data-id="element-1658">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1659">
                From Email
              </label>
              <input type="email" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.fromEmail} onChange={e => setCampaign({
            ...campaign,
            fromEmail: e.target.value
          })} data-id="element-1660" />
            </div>

            <div className="col-span-2" data-id="element-1661">
              <div className="border-b border-gray-200 mb-4" data-id="element-1662">
                <nav className="-mb-px flex gap-4" data-id="element-1663">
                  <button type="button" onClick={() => setContentTab('html')} className={`py-2 px-1 border-b-2 text-sm font-medium ${contentTab === 'html' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-1664">
                    HTML Content
                  </button>
                  <button type="button" onClick={() => setContentTab('text')} className={`py-2 px-1 border-b-2 text-sm font-medium ${contentTab === 'text' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-1665">
                    Plain Text
                  </button>
                </nav>
              </div>

              {contentTab === 'html' ? <CodeEditor label="Email HTML" language="html" value={campaign.htmlContent} onChange={e => setCampaign({
            ...campaign,
            htmlContent: e.target.value
          })} className="min-h-[300px]" data-id="element-1666" /> : <textarea rows={12} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono" value={campaign.textContent} onChange={e => setCampaign({
            ...campaign,
            textContent: e.target.value
          })} placeholder="Enter plain text version..." data-id="element-1667" />}
            </div>
          </div>
        </div>}

      {/* Step 3: Review */}
      {currentStep === 3 && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-6" data-id="element-1668">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-1669">Review Campaign</h2>

          <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-1670">
            <div className="grid grid-cols-2 gap-6" data-id="element-1671">
              <div className="col-span-2" data-id="element-1672">
                <span className="text-sm text-gray-500 block" data-id="element-1673">
                  Campaign Name
                </span>
                <span className="text-sm font-medium text-gray-900" data-id="element-1674">
                  {campaign.name || <span className="text-gray-400 italic" data-id="element-1675">Not set</span>}
                </span>
              </div>
              <div data-id="element-1676">
                <span className="text-sm text-gray-500 block" data-id="element-1677">Subject</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-1678">
                  {campaign.subject}
                </span>
              </div>
              <div data-id="element-1679">
                <span className="text-sm text-gray-500 block" data-id="element-1680">From</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-1681">
                  {campaign.fromName} &lt;{campaign.fromEmail}&gt;
                </span>
              </div>
              <div data-id="element-1682">
                <span className="text-sm text-gray-500 block" data-id="element-1683">
                  Audience Size
                </span>
                <span className="text-sm font-medium text-gray-900" data-id="element-1684">
                  {audienceResult ? `${audienceResult.count} contacts` : 'Not calculated'}
                </span>
              </div>
              <div data-id="element-1685">
                <span className="text-sm text-gray-500 block" data-id="element-1686">Targeting</span>
                <div className="flex flex-wrap gap-1 mt-1" data-id="element-1687">
                  {campaign.targetRegions.length > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1688">
                      {campaign.targetRegions.length} Regions
                    </span>}
                  {campaign.targetMailingLists.length > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800" data-id="element-1689">
                      {campaign.targetMailingLists.length} Lists
                    </span>}
                  {campaign.targetTags.length > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-1690">
                      {campaign.targetTags.length} Tags
                    </span>}
                  {selectedAudienceIds.length > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" data-id="element-1691">
                      {selectedAudienceIds.length} Saved Audience
                      {selectedAudienceIds.length !== 1 ? 's' : ''}
                    </span>}
                </div>
                {selectedAudienceIds.length > 0 && <div className="mt-2 space-y-1" data-id="element-1692">
                    {selectedAudienceIds.map(id => {
                const aud = savedAudiences.find(a => a.id === id);
                if (!aud) return null;
                return <div key={id} className="text-xs text-gray-500 flex items-center gap-1" data-id="element-1693">
                          <Target className="h-3 w-3 text-amber-500" data-id="element-1694" />
                          {aud.name} — {getAudienceRuleSummary(aud)}
                        </div>;
              })}
                  </div>}
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4" data-id="element-1695">
            <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-1696">
              Compliance Checklist
            </h3>
            <div className="space-y-2" data-id="element-1697">
              <div className="flex items-center gap-2 text-sm text-green-700" data-id="element-1698">
                <CheckCircle2 className="h-4 w-4" data-id="element-1699" />
                Sending to confirmed subscribers only
              </div>
              <div className="flex items-center gap-2 text-sm text-green-700" data-id="element-1700">
                <CheckCircle2 className="h-4 w-4" data-id="element-1701" />
                Unsubscribe link included automatically
              </div>
            </div>
          </div>
        </div>}

      {/* Step 4: Export */}
      {currentStep === 4 && !exportResult && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-6" data-id="element-1702">
          <div className="text-center py-4" data-id="element-1703">
            <div className="mx-auto h-16 w-16 bg-purple-100 rounded-full flex items-center justify-center" data-id="element-1704">
              <Database className="h-8 w-8 text-purple-600" data-id="element-1705" />
            </div>
            <h2 className="mt-4 text-2xl font-semibold text-gray-900" data-id="element-1706">
              Push to Zoho CRM
            </h2>
            <p className="mt-2 text-gray-500" data-id="element-1707">
              This will update <strong data-id="element-1708">{audienceResult?.count || 0}</strong>{' '}
              contacts in Zoho CRM with a Bucket ID.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3" data-id="element-1709">
            <div className="flex items-center justify-between" data-id="element-1710">
              <span className="text-sm text-blue-700" data-id="element-1711">Bucket ID:</span>
              <code className="text-sm font-mono bg-white px-2 py-1 rounded border border-blue-200 text-blue-900" data-id="element-1712">
                {bucketConfig.bucketId}
              </code>
            </div>
            <div className="flex items-center justify-between" data-id="element-1713">
              <span className="text-sm text-blue-700" data-id="element-1714">Contacts:</span>
              <span className="text-sm font-medium text-blue-900" data-id="element-1715">
                {audienceResult?.count || 0}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-1716">
              <span className="text-sm text-blue-700" data-id="element-1717">Batches needed:</span>
              <span className="text-sm font-medium text-blue-900" data-id="element-1718">
                {Math.ceil((audienceResult?.count || 0) / 100)}
              </span>
            </div>
          </div>

          <div className="space-y-3" data-id="element-1719">
            <label className="block text-sm font-medium text-gray-700" data-id="element-1720">
              Bucket Expiry (Optional)
            </label>
            <div className="grid grid-cols-4 gap-3" data-id="element-1721">
              {[null, 24, 48, 72].map(hours => <button key={hours || 'none'} onClick={() => setBucketConfig(prev => ({
            ...prev,
            expiryHours: hours
          }))} disabled={isExporting} className={`px-4 py-2 text-sm font-medium rounded-md border ${bucketConfig.expiryHours === hours ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'} disabled:opacity-50`} data-id="element-1722">
                  {hours ? `${hours} hours` : 'No expiry'}
                </button>)}
            </div>
          </div>

          {/* Progress Display */}
          {isExporting && exportProgress && <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3" data-id="element-1723">
              <div className="flex items-center justify-between text-sm" data-id="element-1724">
                <span className="text-purple-700 font-medium" data-id="element-1725">
                  Updating contacts...
                </span>
                <span className="text-purple-900 font-mono text-xs" data-id="element-1726">
                  Batch {exportProgress.currentBatch} /{' '}
                  {exportProgress.totalBatches}
                </span>
              </div>
              <div className="w-full bg-purple-200 rounded-full h-2.5" data-id="element-1727">
                <div className="bg-purple-600 h-2.5 rounded-full transition-all duration-300" style={{
            width: `${Math.round(exportProgress.processedContacts / exportProgress.totalContacts * 100)}%`
          }} data-id="element-1728" />
              </div>
              <div className="flex justify-between text-xs text-purple-600" data-id="element-1729">
                <span data-id="element-1730">
                  {exportProgress.processedContacts} /{' '}
                  {exportProgress.totalContacts} contacts processed
                </span>
                <span data-id="element-1731">
                  {exportProgress.updatedCount} updated
                  {exportProgress.failedCount > 0 ? `, ${exportProgress.failedCount} failed` : ''}
                </span>
              </div>
            </div>}

          {isExporting && !exportProgress && <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center" data-id="element-1732">
              <Loader2 className="h-5 w-5 animate-spin text-purple-600 mx-auto" data-id="element-1733" />
              <p className="text-sm text-purple-700 mt-2" data-id="element-1734">
                Preparing export...
              </p>
            </div>}

          <div className="flex justify-center gap-4 pt-4" data-id="element-1735">
            <button onClick={() => setCurrentStep(3)} disabled={isExporting} className="px-6 py-3 bg-white border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-1736">
              Back
            </button>
            <button onClick={handleExport} disabled={isExporting || !audienceResult?.count} className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 disabled:opacity-50" data-id="element-1737">
              {isExporting ? <>
                  <Loader2 className="h-5 w-5 animate-spin" data-id="element-1738" />
                  Pushing to CRM...
                </> : <>
                  <ExternalLink className="h-5 w-5" data-id="element-1739" />
                  Push to Zoho CRM
                </>}
            </button>
          </div>
        </div>}

      {/* Export Success */}
      {currentStep === 4 && exportResult?.success && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-6" data-id="element-1740">
          <div className="text-center" data-id="element-1741">
            <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center" data-id="element-1742">
              <CheckCircle2 className="h-8 w-8 text-green-600" data-id="element-1743" />
            </div>
            <h2 className="mt-4 text-2xl font-semibold text-gray-900" data-id="element-1744">
              Export Successful!
            </h2>
            <p className="mt-2 text-gray-600" data-id="element-1745">
              <strong data-id="element-1746">{exportResult.updatedCount}</strong> contacts updated in
              Zoho CRM.
            </p>
          </div>

          {exportResult.failedCount > 0 && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-1747">
              <div className="flex items-start gap-2" data-id="element-1748">
                <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-1749" />
                <div data-id="element-1750">
                  <p className="text-sm font-medium text-yellow-900" data-id="element-1751">
                    {exportResult.failedCount} contact(s) failed to update
                  </p>
                  {exportResult.errors?.length > 0 && <ul className="mt-1 text-xs text-yellow-700 space-y-1" data-id="element-1752">
                      {exportResult.errors.slice(0, 5).map((e: string, i: number) => <li key={i} data-id="element-1753">• {e}</li>)}
                      {exportResult.errors.length > 5 && <li data-id="element-1754">...and {exportResult.errors.length - 5} more</li>}
                    </ul>}
                </div>
              </div>
            </div>}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6" data-id="element-1755">
            <h3 className="text-base font-semibold text-blue-900 mb-4" data-id="element-1756">
              Next Steps in Zoho Campaigns
            </h3>
            <ol className="space-y-4 text-sm text-blue-900" data-id="element-1757">
              <li className="flex gap-3" data-id="element-1758">
                <span className="font-semibold" data-id="element-1759">1.</span>
                <span data-id="element-1760">Go to Zoho Campaigns and create a Segment</span>
              </li>
              <li className="flex gap-3" data-id="element-1761">
                <span className="font-semibold" data-id="element-1762">2.</span>
                <div className="flex-1" data-id="element-1763">
                  <p className="mb-2" data-id="element-1764">Use this filter criteria:</p>
                  <div className="bg-white border border-blue-300 rounded p-3 font-mono text-sm flex justify-between items-center" data-id="element-1765">
                    <code data-id="element-1766">Bucket_ID equals {bucketConfig.bucketId}</code>
                    <button onClick={() => handleCopyToClipboard(`Bucket_ID equals ${bucketConfig.bucketId}`, 'bucket-filter')} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200" data-id="element-1767">
                      {copiedItem === 'bucket-filter' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              </li>
              <li className="flex gap-3" data-id="element-1768">
                <span className="font-semibold" data-id="element-1769">3.</span>
                <span data-id="element-1770">
                  Send your campaign, then clear the bucket from Send Buckets
                  page
                </span>
              </li>
            </ol>
          </div>

          <div className="text-center pt-4" data-id="element-1771">
            <button onClick={() => navigate('/campaigns')} className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-1772">
              Return to Campaigns
            </button>
          </div>
        </div>}

      {/* Export Failed */}
      {currentStep === 4 && exportResult && !exportResult.success && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-6" data-id="element-1773">
          <div className="text-center" data-id="element-1774">
            <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center" data-id="element-1775">
              <X className="h-8 w-8 text-red-600" data-id="element-1776" />
            </div>
            <h2 className="mt-4 text-2xl font-semibold text-gray-900" data-id="element-1777">
              Export Failed
            </h2>
            <p className="mt-2 text-gray-500" data-id="element-1778">{exportResult.error}</p>
          </div>
          <div className="text-center" data-id="element-1779">
            <button onClick={() => {
          setExportResult(null);
          setExportProgress(null);
        }} className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-1780">
              Try Again
            </button>
          </div>
        </div>}

      {/* Navigation Buttons (Steps 1-3) */}
      {currentStep < 4 && <div className="flex justify-between pt-6 border-t border-gray-200" data-id="element-1781">
          <button onClick={() => setCurrentStep(Math.max(1, currentStep - 1))} disabled={currentStep === 1} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50" data-id="element-1782">
            Back
          </button>
          <button onClick={() => {
        if (currentStep === 1 && !audienceResult) {
          toast.error('Please build an audience first');
          return;
        }
        if (currentStep === 1 && audienceResult && audienceResult.count === 0) {
          toast.error('Audience is empty — adjust your criteria');
          return;
        }
        setCurrentStep(Math.min(4, currentStep + 1));
      }} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-1783">
            Next Step
            <ArrowRight className="h-4 w-4" data-id="element-1784" />
          </button>
        </div>}
    </div>;
}